import Backbone from 'backbone'
import _ from 'lodash'
import Dataset from 'scoreboard/datasets/model/dataset'
import permissions from 'scoreboard/components/permissions'
import RecordUtil from 'scoreboard/datasets/records/record-util'
const { isSupplementalOnly } = RecordUtil
import FormActionType from './form-action-type'
const { SAVE_RECORD } = FormActionType
import DataType from 'scoreboard/datasets/model/data-type'
import {filterInvalidWidgetReferences} from '../widgets/widgets-view'
import formType from './form-type'
const { DATASET_LINK } = DataType

const Form = Backbone.Model.extend({

	section: 'forms',
	domainType: 'form',

	urlRoot: 'api/forms',

	/** @type {Object<String,Object>} */
	widgetsById: null,
	/** @type {Dataset} */
	dataset: null,

	/**
	 * @constructs
	 * @typedef {Object} Form
	 */
	initialize: function(attributes, options) {
		this.setup(options)
		this.on('sync', () => this.setup(options))
		// #60297 keep track of newly loaded tree items
		this.listenTo(Backbone, 'load:dataset-tree-items', treeItems => {
			let treeItemsById = _.keyBy(treeItems, 'id')
			_.extend(this.treeItemsById, treeItemsById)
		})
	},

	setup: function() {
		this.widgetsById = _.keyBy(this.get('widgets'), 'id')
		this.treeItemsById = _.keyBy(this.get('tree'), 'id')

		// add all tree items for record lists as well
		_.each(this.get('allWidgets'), w => {
			if (w.recordListResponse) {
				_.each(w.recordListResponse.treeItems, ti => {
					this.treeItemsById[ti.id] = ti
				})
			}
		})

		// add all the tree items on the form
		_.each(this.get('treeItems'), ti => {
			this.treeItemsById[ti.id] = ti
		})

		let dataset = this.get('dataset')
		if (dataset) {
			this.dataset = new Dataset(dataset)
		}

		// #59202
		// Dialog form items should use their specific dataset
		let isDialog = this.get('type') === formType.DIALOG
		let dialogDataset = this.get('dialogDataset')
		if (isDialog && dialogDataset) {
			this.dialogDataset = new Dataset(dialogDataset)
		}

		// if we deleted a field that was referenced in a condition, we may need
		// to filter out statements without conditions and without consequences
		// here. It is easier to do it here and wait until the next save than
		// to do it on the deletion of the field
		const newStatements = filterInvalidWidgetReferences({
			statements: _.cloneDeep(this.get('statements')), deletedId: null, deletedTempId: null
		})
		this.set('statements', newStatements)
		const widgets = this.get('widgets')
		_.each(widgets, w => {
			w.statements = filterInvalidWidgetReferences({
				statements: w.statements, deletedId: null, deletedTempId: null
			})
		})
	},

	isEditable: function() {
		return permissions.forms(this.get('organizationId'))
	}
})

const isFieldHiddenForUser = (fieldId, dataset) => {
	const field = dataset.combinedFieldsById[fieldId]
	return !dataset.isModifyDataset() && field.hideFromNonAdmins
}

const isEditableRecord = (fieldId, dataset, record) => {
	const field = dataset.combinedFieldsById[fieldId]
	const isDatasetLink = field?.dataType === DATASET_LINK

	// #53952 if for some reason the field does not exist, return false
	if (_.isNil(field)) return false

	// linked fields don't allow typing, so we can allow calculated fields
	if (field.fieldType === 'calculated' && !isDatasetLink) return false

	if (_.isNil(record)) return true
	return field.editable || isSupplementalOnly(record)
}

/**
 * we can look through the statements before calling them to determine if a
 * save is guaranteed. If it is, we can use this information to disable
 * early for users without the 'modify dataset records permission'
 */
const isSaveGuaranteed = (statements) => {
	const hasSave = (actions) => _.some(actions, a => a.type == SAVE_RECORD)

	// a save is guaranteed if there is at least one else statement in a
	// statement group and every set of consequences has a save in it
	const onlySaves = _.some(statements, sg =>
		!_.some(sg, s => !hasSave(s.consequences)) &&
		_.some(sg, s => _.isEmpty(s.conditions)))

	return _.size(_.flatten(statements)) > 0 && onlySaves
}

export default Form
export { isFieldHiddenForUser }
export { isEditableRecord }
export { isSaveGuaranteed }

Form.__filename__ = 'forms/model/form.js'
